import { useCallback, useState } from "react";
import clinicAPI from "../services/clinicApi";
import { useToast } from "../components/ui";

function isRepostPost(post) {
  return Boolean(post?.repost_source || post?.repost_of_id);
}

export function postEditInitialText(post) {
  if (!post) return "";
  if (isRepostPost(post)) {
    const text = String(post.body || "").trim();
    if (!text || text === " " || text.startsWith("Поделился записью:")) {
      return "";
    }
    return text;
  }
  return String(post.body || "");
}

async function updateClientPost(post, payload, apiMode = "client") {
  if (post.scope === "wall") {
    if (apiMode === "staff") {
      return clinicAPI.updateStaffWallPost(post.id, payload);
    }
    return clinicAPI.updateWallPost(post.id, payload);
  }
  if (apiMode === "staff") {
    return clinicAPI.updateClubLifePost(post.id, payload);
  }
  return clinicAPI.updateMyClubLifePost(post.id, payload);
}

async function deleteClientPost(post, apiMode = "client") {
  if (post.scope === "wall") {
    if (apiMode === "staff") {
      return clinicAPI.deleteStaffWallPost(post.id);
    }
    return clinicAPI.deleteWallPost(post.id);
  }
  if (apiMode === "staff") {
    return clinicAPI.deleteClubLifePost(post.id);
  }
  return clinicAPI.deleteMyClubLifePost(post.id);
}

export function useClientPostManage(setPosts, { apiMode = "client" } = {}) {
  const toast = useToast();
  const [menuPost, setMenuPost] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [editPost, setEditPost] = useState(null);
  const [busy, setBusy] = useState(false);

  const openMenu = useCallback((post, event) => {
    setMenuPost(post);
    setMenuAnchor(event?.currentTarget || null);
  }, []);
  const closeMenu = useCallback(() => {
    if (!busy) {
      setMenuPost(null);
      setMenuAnchor(null);
    }
  }, [busy]);

  const openEdit = useCallback((post) => {
    setMenuPost(null);
    setMenuAnchor(null);
    setEditPost(post);
  }, []);

  const closeEdit = useCallback(() => {
    if (!busy) setEditPost(null);
  }, [busy]);

  const applyUpdatedPost = useCallback(
    (updated) => {
      if (!updated?.id) return;
      setPosts((prev) =>
        prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)),
      );
    },
    [setPosts],
  );

  const removePost = useCallback(
    (postId) => {
      setPosts((prev) => prev.filter((p) => p.id !== postId));
    },
    [setPosts],
  );

  const confirmDelete = useCallback(
    async (post) => {
      if (!post || busy) return;
      setBusy(true);
      try {
        await deleteClientPost(post, apiMode);
        removePost(post.id);
        setMenuPost(null);
        setMenuAnchor(null);
        toast.success("Запись удалена");
      } catch (err) {
        toast.error(err.response?.data?.error || "Не удалось удалить");
      } finally {
        setBusy(false);
      }
    },
    [busy, removePost, toast, apiMode],
  );

  const confirmEdit = useCallback(
    async (post, payload) => {
      if (!post || busy) return;
      setBusy(true);
      try {
        const data = await updateClientPost(post, payload, apiMode);
        applyUpdatedPost(data.post);
        setEditPost(null);
        toast.success("Запись обновлена");
      } catch (err) {
        toast.error(err.response?.data?.error || "Не удалось сохранить");
      } finally {
        setBusy(false);
      }
    },
    [applyUpdatedPost, busy, toast, apiMode],
  );

  return {
    menuPost,
    menuAnchor,
    editPost,
    busy,
    openMenu,
    closeMenu,
    openEdit,
    closeEdit,
    confirmDelete,
    confirmEdit,
  };
}
